class BrandStorefront extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.abortController = new AbortController();

    this.setupReveals();
    this.setupParallax();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.revealObserver?.disconnect();
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  setupReveals() {
    const items = Array.from(this.querySelectorAll('[data-reveal]'));
    if (!items.length || this.reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.classList.add('brand-motion-ready');
    this.revealObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    });

    items.forEach((item, index) => {
      item.style.transitionDelay = String(Math.min(index % 4, 3) * 70) + 'ms';
      this.revealObserver.observe(item);
    });
  }

  setupParallax() {
    if (this.reduceMotion) return;
    this.hero = this.querySelector('[data-brand-hero]');
    if (!this.hero) return;

    const signal = this.abortController.signal;
    const update = () => {
      const rect = this.hero.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, -rect.top / Math.max(rect.height, 1)));
      this.style.setProperty('--brand-parallax-y', String(progress * 22) + 'px');
      this.scrollFrame = null;
    };

    const requestUpdate = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true, signal });
    window.addEventListener('resize', requestUpdate, { passive: true, signal });
    update();
  }
}

class LuneaDiagnostic extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs = Array.from(this.querySelectorAll('[data-diagnostic-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-diagnostic-panel]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.diagnosticTab), { signal });
      tab.addEventListener('keydown', (event) => this.handleTabKeydown(event), { signal });
    });

    this.querySelectorAll('[data-diagnostic-form]').forEach((form) => {
      form.addEventListener('submit', (event) => this.handleSubmit(event), { signal });
    });

    this.querySelectorAll('[data-diagnostic-reset]').forEach((button) => {
      button.addEventListener('click', () => this.reset(button), { signal });
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  activate(name) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.diagnosticTab === name;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.diagnosticPanel !== name;
    });
  }

  handleTabKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const next = this.tabs[(currentIndex + direction + this.tabs.length) % this.tabs.length];
    this.activate(next.dataset.diagnosticTab);
    next.focus();
  }

  handleSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    if (!form.reportValidity()) return;

    const type = form.dataset.diagnosticForm;
    const values = Object.fromEntries(new FormData(form).entries());
    const result = this.querySelector(`[data-diagnostic-result="${type}"]`);
    if (!result) return;

    const recommendation = type === 'foundation'
      ? this.foundationRecommendation(values)
      : this.routineRecommendation(values);

    const title = result.querySelector('[data-result-title]');
    const copy = result.querySelector('[data-result-copy]');
    if (title) title.textContent = recommendation.title;
    if (copy) copy.textContent = recommendation.copy;

    form.hidden = true;
    result.hidden = false;
    result.focus({ preventScroll: true });
    result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  }

  foundationRecommendation(values) {
    const finish = {
      naturel: ['Teint seconde peau', 'un fini naturel qui laisse vivre le grain de peau'],
      lumineux: ['Voile lumière', 'un fini lumineux qui capte la lumière sans effet brillant'],
      velours: ['Teint velours', 'un fini mat doux qui floute sans figer']
    }[values.finish] || ['Teint sur mesure', 'un fini équilibré'];
    const coverage = {
      legere: 'Privilégiez une couvrance légère, appliquée du centre vers l’extérieur.',
      modulable: 'Construisez la couvrance par voiles fins uniquement là où elle est nécessaire.',
      haute: 'Travaillez une couvrance ciblée, puis fondez les contours pour garder du naturel.'
    }[values.coverage] || '';
    const undertone = {
      froid: 'Orientez-vous vers les teintes aux nuances rosées ou froides.',
      neutre: 'Choisissez une teinte équilibrée, sans dominante jaune ni rosée.',
      chaud: 'Préférez les nuances dorées, chaudes ou olive.'
    }[values.undertone] || '';

    return { title: finish[0], copy: `Votre accord idéal : ${finish[1]}. ${coverage} ${undertone}` };
  }

  routineRecommendation(values) {
    const goal = {
      eclat: ['Rituel lumière', 'base éclat, teint léger, blush frais et touche illuminatrice'],
      uniformite: ['Rituel peau nette', 'correction ciblée, teint modulable, poudre fine et lèvres naturelles'],
      definition: ['Rituel définition', 'teint transparent, sourcils structurés, regard intensifié et bouche équilibrée']
    }[values.goal] || ['Rituel essentiel', 'teint, regard et lèvres'];
    const time = {
      express: 'en 3 étapes rapides',
      essentielle: 'en 5 étapes équilibrées',
      complete: 'en 7 étapes, du teint aux finitions'
    }[values.time] || 'en quelques gestes';
    const texture = {
      fraiche: 'avec des textures légères et fraîches',
      equilibree: 'avec des textures fines au confort discret',
      confort: 'avec des textures souples et enveloppantes'
    }[values.texture] || 'avec des textures confortables';

    return { title: goal[0], copy: `Votre routine : ${goal[1]}, ${time}, ${texture}. Commencez par peu de matière et ajoutez uniquement ce qui sert le résultat.` };
  }

  reset(button) {
    const result = button.closest('[data-diagnostic-result]');
    const panel = button.closest('[data-diagnostic-panel]');
    const form = panel?.querySelector('[data-diagnostic-form]');
    if (!result || !form) return;
    result.hidden = true;
    form.hidden = false;
    form.reset();
    form.querySelector('input')?.focus();
  }
}

if (!customElements.get('brand-storefront')) {
  customElements.define('brand-storefront', BrandStorefront);
}

if (!customElements.get('lunea-diagnostic')) {
  customElements.define('lunea-diagnostic', LuneaDiagnostic);
}


function setupStudioCommerceMotion() {
  const cards = Array.from(document.querySelectorAll('body.template-404.brand-siecle product-card'));
  if (!cards.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || !('IntersectionObserver' in window)) {
    cards.forEach((card) => card.classList.add('studio-in'));
    return;
  }
  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const card = entry.target;
      const index = cards.indexOf(card);
      card.style.transitionDelay = `${Math.min(index % 4, 3) * 85}ms`;
      card.classList.add('studio-in');
      obs.unobserve(card);
    });
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
  cards.forEach((card) => observer.observe(card));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupStudioCommerceMotion, { once: true });
} else {
  setupStudioCommerceMotion();
}

function storefrontBrand() {
  return document.documentElement.dataset.storefrontBrand || document.body?.dataset?.brand || '';
}

function isInternalNavigableHref(href) {
  return href && !href.startsWith('#') && !href.startsWith('mailto:') && !href.startsWith('tel:') && !href.startsWith('javascript:');
}

function shouldUseSiecleShop(anchor) {
  const label = (anchor.textContent || anchor.getAttribute('aria-label') || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('fr');
  const rawHref = (anchor.getAttribute('href') || '').trim();
  if (!isInternalNavigableHref(rawHref)) return false;

  let pathname = rawHref.toLocaleLowerCase('fr');
  try { pathname = new URL(rawHref, window.location.origin).pathname.toLocaleLowerCase('fr'); } catch (_) {}

  const shopLabel = /(boutique|voir le drop|découvrir les collections|decouvrir les collections|voir notre boutique|tous les produits|continuer les achats|continue shopping|shop)/i.test(label);
  const staleShopPath = /\/(pages|collections)\/(boutique|shop|store|catalogue)\/?$/i.test(pathname);
  return shopLabel || staleShopPath;
}

function repairStorefrontLinks(scope = document) {
  const brand = storefrontBrand();
  const routes = window.STOREFRONT_ROUTES || {};
  const safeSiecleShop = routes.siecleShop || '/collections/all';
  const safeLuneaShop = routes.luneaShop || '/collections?view=lunea';

  scope.querySelectorAll?.('a[href]').forEach((anchor) => {
    const href = (anchor.getAttribute('href') || '').trim();
    if (!isInternalNavigableHref(href)) return;

    const label = (anchor.textContent || anchor.getAttribute('aria-label') || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase('fr');

    if (brand === 'siecle' && shouldUseSiecleShop(anchor)) {
      anchor.setAttribute('href', safeSiecleShop);
      anchor.dataset.siecleShopRoute = 'true';
      return;
    }

    if (brand === 'lunea' && /\b(boutique|tous les produits|tout le maquillage|voir notre boutique)\b/i.test(label)) {
      const url = new URL(safeLuneaShop, window.location.origin);
      if (!url.searchParams.has('view')) url.searchParams.set('view', 'lunea');
      anchor.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    }
  });
}

function setupStorefrontRouteGuard() {
  repairStorefrontLinks();

  // Capture navigation before theme drawers/animations can follow a stale menu URL.
  document.addEventListener('click', (event) => {
    if (storefrontBrand() !== 'siecle') return;
    const anchor = event.target.closest?.('a[href]');
    if (!anchor || !shouldUseSiecleShop(anchor)) return;
    const safeSiecleShop = window.STOREFRONT_ROUTES?.siecleShop || '/collections/all';
    if ((anchor.getAttribute('href') || '') === safeSiecleShop) return;
    event.preventDefault();
    window.location.assign(safeSiecleShop);
  }, true);

  // Shopify can hydrate/re-render sections after load, so repair new links too.
  if ('MutationObserver' in window) {
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches?.('a[href]')) repairStorefrontLinks(node.parentElement || node);
          else repairStorefrontLinks(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', (event) => repairStorefrontLinks(event.target));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupStorefrontRouteGuard, { once: true });
} else {
  setupStorefrontRouteGuard();
}

